import type { FeedResponse, Photo } from './api';

/**
 * Small, private cache for the first visible feed page.
 *
 * Media bytes live in the separately bounded service-worker cache. This store
 * only keeps enough authorized metadata to paint the feed immediately while a
 * real Nextcloud refresh runs in the background.
 */
const DATABASE_NAME = 'family-photos-private-feed-v1';
const STORE_NAME = 'feeds';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_PHOTOS = 12;
const MAX_MOMENT_PHOTOS = 4;

interface FeedCacheEntry {
  key: string;
  userId: string;
  view: 'family' | 'mine';
  cachedAt: number;
  expiresAt: number;
  response: FeedResponse;
}

export interface CachedFeed {
  response: FeedResponse;
  cachedAt: number;
}

export async function readCachedFeed(
  userId: string,
  view: 'family' | 'mine',
): Promise<CachedFeed | null> {
  const database = await openDatabase();
  if (!database) return null;

  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(feedKey(userId, view));
    request.onsuccess = () => {
      const entry = request.result as FeedCacheEntry | undefined;
      if (
        !entry
        || entry.userId !== userId
        || entry.view !== view
        || entry.expiresAt <= Date.now()
      ) {
        if (entry) store.delete(entry.key);
        resolve(null);
        return;
      }
      const response = sanitizeFeed(entry.response);
      resolve(response ? { response, cachedAt: entry.cachedAt } : null);
    };
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => { database.close(); resolve(null); };
    transaction.onabort = () => { database.close(); resolve(null); };
  });
}

export async function writeCachedFeed(
  userId: string,
  view: 'family' | 'mine',
  response: FeedResponse,
): Promise<void> {
  if (!userId) return;
  const safe = sanitizeFeed(response);
  if (!safe) return;
  const database = await openDatabase();
  if (!database) return;
  const now = Date.now();
  await runTransaction(database, (store) => store.put({
    key: feedKey(userId, view),
    userId,
    view,
    cachedAt: now,
    expiresAt: now + CACHE_TTL_MS,
    response: safe,
  } satisfies FeedCacheEntry));
}

export async function clearFeedCache(): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => store.clear());
}

export async function purgeFeedCacheExcept(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, (store) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      const value = cursor.value as Partial<FeedCacheEntry>;
      if (value.userId !== userId) cursor.delete();
      cursor.continue();
    };
  });
}

function sanitizeFeed(response: FeedResponse): FeedResponse | null {
  if (!response || !Array.isArray(response.items) || !Array.isArray(response.moments)) return null;
  const items = response.items.map(sanitizePhoto).filter((photo): photo is Photo => photo !== null).slice(0, MAX_PHOTOS);
  const moments = response.moments
    .filter((moment) => moment?.kind === 'on_this_day' || moment?.kind === 'rediscover')
    .map((moment) => ({
      kind: moment.kind,
      title: String(moment.title ?? ''),
      detail: String(moment.detail ?? ''),
      photos: Array.isArray(moment.photos)
        ? moment.photos.map(sanitizePhoto).filter((photo): photo is Photo => photo !== null).slice(0, MAX_MOMENT_PHOTOS)
        : [],
    }));
  return {
    items,
    moments,
    nextCursor: typeof response.nextCursor === 'string' ? response.nextCursor : null,
  };
}

function sanitizePhoto(photo: Photo): Photo | null {
  if (
    !photo
    || typeof photo.fileId !== 'string'
    || !photo.fileId
    || typeof photo.name !== 'string'
    || typeof photo.lastModified !== 'number'
    || typeof photo.contentType !== 'string'
  ) return null;
  return {
    ...(typeof photo.postId === 'string' ? { postId: photo.postId } : {}),
    fileId: photo.fileId,
    name: photo.name,
    path: typeof photo.path === 'string' ? photo.path : '',
    contentType: photo.contentType,
    size: Number.isFinite(photo.size) ? photo.size : 0,
    lastModified: photo.lastModified,
    hasPreview: Boolean(photo.hasPreview),
    owner: typeof photo.owner === 'string' ? photo.owner : 'Family',
    isVideo: Boolean(photo.isVideo),
    ...(typeof photo.album === 'string' ? { album: photo.album } : {}),
    ...(photo.audience && typeof photo.audience.id === 'string' && typeof photo.audience.label === 'string'
      ? { audience: { id: photo.audience.id, label: photo.audience.label } }
      : {}),
    likeCount: Number.isFinite(photo.likeCount) ? Math.max(0, Number(photo.likeCount)) : 0,
    likedByMe: Boolean(photo.likedByMe),
    ...(typeof photo.caption === 'string' ? { caption: photo.caption } : {}),
  };
}

function feedKey(userId: string, view: 'family' | 'mine'): string {
  return `${encodeURIComponent(userId)}:${view}`;
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

function runTransaction(
  database: IDBDatabase,
  operation: (store: IDBObjectStore) => IDBRequest | void,
): Promise<void> {
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    try {
      operation(transaction.objectStore(STORE_NAME));
    } catch {
      database.close();
      resolve();
      return;
    }
    transaction.oncomplete = () => { database.close(); resolve(); };
    transaction.onerror = () => { database.close(); resolve(); };
    transaction.onabort = () => { database.close(); resolve(); };
  });
}
